import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, TextField, InputAdornment, styled, Stack, Drawer, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import { openContact, openModal } from '../../../redux/slices/profileSlice';
import CustomModal from '../../CustomModal';
import userLock from '../../../assets/user-lock.png';
import Image from '../../Image';
import MalaysiaFlag from '../../../assets/profile/flag.png';
import useAuth from '../../../hooks/useAuth';
import Iconify from '../../Iconify';
import useLocales from '../../../hooks/useLocales';
import NonRootHeader from '../../../layouts/dashboard/header/NonRootHeader';
import useResponsive from '../../../hooks/useResponsive';
import Page from '../../Page';
import { ThemeButton, ThemeButtonOutlined } from '../../settings/GlobalStyled';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';

/* ------------------- Styled Components ------------------- */

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  py: 16,
  px: 24,
});

const CenterImageBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 14,
    backgroundColor: '#F6F7F9',
    height: 48,

    '&:hover fieldset': {
      border: '1px solid #D1D5DB',
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiOutlinedInput-root': {
      height: 36,
    },
  },

  '& input': {
    fontSize: 14,
    fontWeight: 400,
    color: '#111827',
  },

  '& input::placeholder': {
    color: '#9CA3AF',
    fontSize: 14,
  },

  '& .MuiInputAdornment-root': {
    marginRight: 24,
  },
}));

const OtpStack = styled(Stack)({
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: 8,
});

const OtpInput = styled('input', {
  shouldForwardProp: (prop) => prop !== 'error',
})(({ error, theme }) => ({
  width: 52,
  height: 52,
  borderRadius: '8px',

  border: error ? '1.5px solid #FF4842' : '1px solid transparent',
  background: error ? '#FFE6E6' : '#F3F4F6',

  textAlign: 'center',
  fontSize: 18,
  fontWeight: 600,
  color: '#111827',

  outline: 'none',
  transition: 'all 0.2s ease',

  '&:focus': {
    background: '#FFFFFF',
    border: error ? '1.5px solid #FF4842' : `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 2px ${theme.palette.primary.lighter}`, // 👈 nice glow
  },

  '&:disabled': {
    background: '#E5E7EB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
  },
}));

const LinkText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.primary.main,
  textAlign: 'right',
  cursor: 'pointer',
  fontWeight: 400,
  textDecoration: 'underline',
}));

const FooterText = styled(Typography)({
  fontSize: 13,
  color: '#919EAB',
  textAlign: 'center',
});

const ActionButtons = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '16px',
  marginBottom: 24,
});

const ContinueButton = styled(ThemeButton)(({ theme }) => ({
  // Add custom styling
  height: 36,
  minHeight: 'unset',
}));

const CancelButton = styled(ThemeButtonOutlined)({
  // Add custom styling
  height: 36,
  minHeight: 'unset',
});

const ResetPasswordDialog = ({ handleClose, handleOpen }) => {
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');
  const theme = useTheme();
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const { requestOtpResetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [timeLeft, setTimeLeft] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [isOtpError, setIsOtpError] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const isValidPhone = phone.length >= 9;
  const isOtpComplete = otp.every((digit) => digit !== '');

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    setIsOtpError(false);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pasteData)) return;

    const pasteArray = pasteData.split('');
    const newOtp = [...otp];

    pasteArray.forEach((digit, index) => {
      if (index < 6) newOtp[index] = digit;
    });

    setOtp(newOtp);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const finalOtp = otp.join('');

      if (verificationCode !== finalOtp) {
        setInfoMessage('Please enter the correct code');
        setIsOtpError(true);
        return;
      }

      dispatch(
        openModal({
          view: 'new-password',
          data: finalOtp,
        })
      );

      clearForm();
    } catch (error) {
      console.log('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setPhone('');
    setOtp(['', '', '', '', '', '']);
    setVerificationCode('');
    setInfoMessage('');
    setIsOtpError(false);
    setRecaptchaToken('');
    setTimeLeft(0);
    setOtpExpiry(null);
    localStorage.removeItem('otpExpiry');
    localStorage.removeItem('otpPhone');
  };

  const handleContactUs = () => {
    handleClose();
    dispatch(openContact());
    navigate('/');
  };

  const handleRequestOtp = async () => {
    if (!isValidPhone) {
      enqueueSnackbar(translate('enter_valid_phone'), { variant: 'warning' });
      return;
    }

    if (timeLeft > 0 || isLoading) return;

    setIsLoading(true);

    try {
      // const recaptchaToken = '41234123';

      const formattedPhone = phone.startsWith('0') ? phone.substring(1) : phone;

      const fullPhoneNumber = `60${formattedPhone}`;

      const response = await requestOtpResetPassword(fullPhoneNumber, recaptchaToken);

      if (response?.success) {
        enqueueSnackbar('OTP sent successfully!', { variant: 'success' });
        setVerificationCode(response?.code.toString());
        const expiry = Date.now() + 120 * 1000;
        localStorage.setItem('otpExpiry', expiry);
        localStorage.setItem('otpPhone', phone);

        const remaining = Math.floor((expiry - Date.now()) / 1000);
        setTimeLeft(remaining);

        const interval = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        const message = response?.error?.[0] || response?.message || 'Failed to send OTP';
        enqueueSnackbar(message, { variant: 'error' });
      }
    } catch (error) {
      const message =
        error?.response?.data?.error?.[0] ||
        error?.response?.data?.message ||
        'Something went wrong. Please try again.';

      console.log(message);

      // ✅ show error snackbar
      enqueueSnackbar(message, { variant: 'error' });

      if (message === 'Contact number is not register in the system.') {
        clearForm();

        dispatch(
          openModal({
            view: 'hint-reset',
          })
        );
      }

      console.error('Request OTP Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedExpiry = localStorage.getItem('otpExpiry');
    const savedPhone = localStorage.getItem('otpPhone');

    if (!savedExpiry || !savedPhone) return;

    const remaining = Math.max(0, Math.floor((Number(savedExpiry) - Date.now()) / 1000));
    if (remaining <= 0) return;

    setPhone(savedPhone);
    setTimeLeft(remaining);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!otpExpiry) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((otpExpiry - Date.now()) / 1000));

      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        localStorage.removeItem('otpExpiry');
        setOtpExpiry(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpExpiry]);

  let requestOtpLabel = translate('request_otp');

  if (isLoading) {
    requestOtpLabel = translate('sending');
  } else if (timeLeft > 0) {
    requestOtpLabel = `${translate('resend_in')} ${timeLeft}s`;
  }

  const isOtpDisabled = !verificationCode || isLoading || loading;

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          title={translate('reset_password')}
          actions={
            <ActionButtons>
              {/* Continue Button */}
              <ContinueButton fullWidth disabled={!isValidPhone || !isOtpComplete} onClick={handleSubmit}>
                {translate('continue')}
              </ContinueButton>

              {/* Cancel */}
              <CancelButton onClick={handleClose}>{translate('cancel')}</CancelButton>

              <FooterText onClick={handleContactUs}>
                {translate('not_received_otp')}{' '}
                <span
                  style={{
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    fontWeight: 400,
                    textDecoration: 'underline',
                  }}
                >
                  {translate('contact_us')}
                </span>
              </FooterText>
            </ActionButtons>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2, px: 3 }}>
            <CenterImageBox>
              <Image src={userLock} alt="reset-password" style={{ width: 150 }} />
            </CenterImageBox>

            <Box>
              <Typography variant="body2" fontWeight={500}>
                {translate('mobile')}
                <span style={{ color: 'red' }}>*</span>
              </Typography>

              <StyledTextField
                type="tel"
                autoComplete="tel"
                placeholder="00 000 0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={MalaysiaFlag} alt="Malaysia" width={24} height={16} />
                        <Typography sx={{ fontSize: 14, color: '#374151' }}>+60</Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={(token) => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken('')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              />
              {/* {formError?.recaptcha && (
                <Typography sx={{ color: '#FF4842', fontSize: '12px', mt: 0.5 }}>{formError.recaptcha}</Typography>
              )} */}
            </Box>

            {/* OTP Field */}
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {translate('enter_code')}
                <span style={{ color: 'red' }}>*</span>
              </Typography>

              <OtpStack direction="row" mt={1}>
                {otp.map((digit, index) => (
                  <OtpInput
                    disabled={isOtpDisabled}
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="number"
                    maxLength={1}
                    value={digit}
                    error={isOtpError}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otp[index] && index > 0) {
                        inputRefs.current[index - 1].focus();
                      }
                    }}
                    onPaste={handlePaste}
                  />
                ))}
              </OtpStack>

              {infoMessage && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Iconify icon="mdi:information-outline" sx={{ color: '#FF4842', width: 16 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#FF4842',
                      fontWeight: 600,
                    }}
                  >
                    {infoMessage}
                  </Typography>
                </Box>
              )}

              <LinkText
                onClick={handleRequestOtp}
                sx={{
                  pointerEvents: timeLeft > 0 || isLoading ? 'none' : 'auto',
                  opacity: timeLeft > 0 || isLoading ? 0.5 : 1,
                }}
              >
                {requestOtpLabel}
              </LinkText>
            </Box>
          </Box>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          PaperProps={{
            sx: {
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            },
            margin: 'auto',
            height: { sm: 'calc(100vh - 64px)', xs: 'calc(100% - (64px + 58px))' },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
              <Iconify icon="mdi:close" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('reset-password')}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              background: isSafariOrIOS ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3,
              px: { xs: 'unset', sm: 2 },
              py: { xs: '10', sm: 'unset' },
              overflowY: 'scroll',
            }}
          >
            <Page
              title="Reset Password"
              sx={{
                height: '100%',
                width: '100%',
                // overflowY: "scroll"
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  // display: 'flex',
                  // alignItems: 'center',
                  // justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: 400, md: '100%' },
                    maxWidth: { xs: 'unset', sm: 400 },
                    mx: 'auto',
                    mt: 1,
                    // transform: {
                    //   xs: 'scale(0.8)',
                    //   sm: 'scale(0.9)',
                    //   md: 'scale(1)',
                    // },
                    transformOrigin: 'center center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '10px',
                  }}
                >
                  <Box sx={{ p: 3, borderRadius: 1, backgroundColor: 'white', width: { xs: '80%', sm: 'unset' } }}>
                    <img
                      src={userLock}
                      alt="login-icon"
                      style={{ width: '30vw', maxWidth: 160, margin: '0 auto 24px', display: 'block' }}
                    />

                    <Stack spacing={2}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                          {translate('mobile')} <span style={{ color: 'red' }}>*</span>
                        </Typography>

                        <StyledTextField
                          placeholder="00 000 0000"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <img src={MalaysiaFlag} alt="Malaysia" width={24} height={16} />

                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 400,
                                      color: '#374151',
                                    }}
                                  >
                                    +60
                                  </Typography>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken('')}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        className="recaptcha"
                      />

                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                          {translate('enter_code')} <span style={{ color: 'red' }}>*</span>
                        </Typography>

                        <OtpStack direction="row" mt={1}>
                          {otp.map((digit, index) => (
                            <OtpInput
                              sx={{
                                width: 36,
                                height: 36,
                              }}
                              disabled={isOtpDisabled}
                              key={index}
                              ref={(el) => {
                                inputRefs.current[index] = el;
                              }}
                              type="number"
                              maxLength={1}
                              value={digit}
                              error={isOtpError}
                              onChange={(e) => handleOtpChange(e.target.value, index)}
                              onKeyDown={(e) => {
                                if (e.key === 'Backspace' && !otp[index] && index > 0) {
                                  inputRefs.current[index - 1].focus();
                                }
                              }}
                              onPaste={handlePaste}
                            />
                          ))}
                        </OtpStack>

                        {infoMessage && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Iconify icon="mdi:information-outline" sx={{ color: '#FF4842', width: 16 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#FF4842',
                                fontWeight: 600,
                              }}
                            >
                              {infoMessage}
                            </Typography>
                          </Box>
                        )}

                        <LinkText
                          onClick={handleRequestOtp}
                          sx={{
                            pointerEvents: timeLeft > 0 || isLoading ? 'none' : 'auto',
                            opacity: timeLeft > 0 || isLoading ? 0.5 : 1,
                            fontSize: ' 11px ',
                          }}
                        >
                          {requestOtpLabel}
                        </LinkText>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          gap: 2,
                          mb: { xs: 0, mb: 6 },
                        }}
                      >
                        <ContinueButton
                          variant="contained"
                          fullWidth
                          disabled={!isValidPhone || !isOtpComplete}
                          onClick={handleSubmit}
                        >
                          {translate('continue')}
                        </ContinueButton>

                        <CancelButton variant="outlined" fullWidth onClick={handleClose}>
                          {translate('cancel')}
                        </CancelButton>

                        <FooterText sx={{ fontSize: '11px' }} onClick={handleContactUs}>
                          {translate('not_received_otp')}{' '}
                          <span
                            style={{
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              fontWeight: 600,
                              textDecoration: 'underline',
                              fontSize: '11px',
                            }}
                          >
                            {translate('contact_us')}
                          </span>
                        </FooterText>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Page>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default ResetPasswordDialog;
